import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-16989 — what a Workd amount posts to in QuickBooks.
 *
 * - `item` — sales of one item (sourceKey = item id).
 * - `itemDefault` — sales of any item without its own mapping, and any invoice
 *   amount not carried by a line.
 * - `paymentMethodType` — money received by one payment method type
 *   (sourceKey = the method type, e.g. "ACH", "Credit Card").
 * - `receivableHolding` — where unpaid sales sit until they are paid.
 * - `cardSurcharge`, `salesTax`, `discount`, `freight` — the matching invoice
 *   and payment amounts.
 */
export enum QboGlMappingSourceType {
	ITEM = 'item',
	ITEM_DEFAULT = 'itemDefault',
	PAYMENT_METHOD_TYPE = 'paymentMethodType',
	RECEIVABLE_HOLDING = 'receivableHolding',
	CARD_SURCHARGE = 'cardSurcharge',
	SALES_TAX = 'salesTax',
	DISCOUNT = 'discount',
	FREIGHT = 'freight',
}

/**
 * PD-16989 — one GL mapping for QuickBooks summary posting.
 *
 * Single-value types (everything but item and payment method type) store an
 * empty sourceKey rather than NULL so the unique index also stops duplicates
 * for them (MySQL treats NULLs as distinct in a unique index).
 */
@Index('qbo_gl_mapping_source_uq', ['sourceType', 'sourceKey'], {
	unique: true,
})
@Entity('qbo_gl_mapping')
export class QboGlMappingEntity extends BaseEntity {
	@Column('varchar', { name: 'sourceType', length: 32 })
	public sourceType!: QboGlMappingSourceType;

	@Column('varchar', { name: 'sourceKey', length: 128, default: () => "''" })
	public sourceKey!: string;

	/** QuickBooks Account.Id (qbo_account.qboId). */
	@Column('varchar', { name: 'qboAccountId', length: 32 })
	public qboAccountId!: string;
}
